import WarningAmber from "@mui/icons-material/WarningAmber";
import { Stack, Tooltip, Typography } from "@mui/material";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";
import { Issue, IssueLevel } from "generated-graphql/graphql";
import { ComponentProps } from "react";

type V2DisplayOnlyFieldProps = ComponentProps<typeof DisplayOnlyField> & {
  issues?: Issue[];
};

/**
 * DisplayOnlyField with 8px label spacing and wrapping values for the V2 facility profile.
 *
 * When `issues` are provided:
 * - CRITICAL issues (required) render as red error text below the value
 * - ERROR issues (compliance) render as an orange warning icon with "Needs attention" text,
 *   with a tooltip listing the issues on hover
 */
export function V2DisplayOnlyField({ issues, ...props }: V2DisplayOnlyFieldProps) {
  const criticalIssues = issues?.filter((i) => i.level === IssueLevel.Critical) ?? [];
  const errorIssues = issues?.filter((i) => i.level === IssueLevel.Error) ?? [];

  return (
    <Stack spacing={0}>
      <DisplayOnlyField labelSpacing={1} wrapValue {...props} />
      {criticalIssues.length > 0 && (
        <Stack spacing={0}>
          {criticalIssues.map((issue, i) => (
            <Typography key={i} variant="body2" color="error" fontSize="0.75rem">
              {issue.message}
            </Typography>
          ))}
        </Stack>
      )}
      {errorIssues.length > 0 && (
        <Tooltip
          title={
            <Stack spacing={0.5}>
              {errorIssues.map((issue, i) => (
                <Typography key={i} variant="body2" fontSize="12px">
                  {issue.message}
                </Typography>
              ))}
            </Stack>
          }
          arrow
          placement="bottom-start"
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ cursor: "default", mt: 0.5, width: "fit-content" }}
          >
            <WarningAmber sx={{ fontSize: "1rem", color: "warning.main" }} />
            <Typography variant="body2" fontSize="0.75rem" color="warning.main" fontStyle="italic">
              needs attention
            </Typography>
          </Stack>
        </Tooltip>
      )}
    </Stack>
  );
}
